/**
 * Slack Channel Adapter
 *
 * Uses Slack Bolt framework or Events API via Socket Mode
 */

import type { WebSocket } from "ws";
import {
	BaseChannelAdapter,
	globalChannelRegistry,
	type ChannelConfig,
	type ChannelProfile,
} from "./base.js";
import type { InboundMessage, OutboundMessage, SenderInfo } from "../types/index.js";

/** Slack channel configuration */
export interface SlackConfig {
	/** Bot token (xoxb-...) */
	botToken: string;
	/** App token for Socket Mode (xapp-...) */
	appToken?: string;
	/** Signing secret for HTTP mode */
	signingSecret?: string;
	/** Use Socket Mode (default: true) */
	socketMode?: boolean;
	/** Port for HTTP mode */
	port?: number;
}

/** Slack adapter using Events API */
class SlackAdapter extends BaseChannelAdapter {
	readonly channelType = "slack";

	private botToken: string;
	private appToken?: string;
	private socketMode: boolean;
	private ws?: WebSocket;
	private reconnectAttempts = 0;
	private maxReconnectAttempts = 5;
	private botInfo?: SlackUser;
	private socketUrl?: string;

	constructor(config: ChannelConfig) {
		super(config);
		const slackConfig = config.config as unknown as SlackConfig;
		this.botToken = slackConfig.botToken;
		this.appToken = slackConfig.appToken;
		this.socketMode = slackConfig.socketMode ?? true;
	}

	/** Initialize the adapter */
	async initialize(): Promise<void> {
		// Get bot info
		const response = await this.apiCall<{ ok: boolean; error?: string; user_id: string; user: string }>("auth.test");
		if (!response.ok) {
			throw new Error(`Failed to initialize Slack bot: ${response.error}`);
		}

		this.botInfo = {
			id: response.user_id,
			name: response.user,
		};
		console.log(`Slack bot initialized: @${this.botInfo.name}`);

		if (this.socketMode && this.appToken) {
			await this.connectSocketMode();
		} else {
			// HTTP mode would require an Express server
			throw new Error("HTTP mode not implemented. Please use Socket Mode with appToken.");
		}
	}

	/** Shutdown the adapter */
	async shutdown(): Promise<void> {
		this.connected = false;
		this.ws?.close();
	}

	/** Send a message to Slack */
	async send(message: OutboundMessage): Promise<void> {
		const channel = message.target;

		for (const part of message.parts) {
			switch (part.type) {
				case "text": {
					// Slack max message length is 4000 for blocks, but using text is safer
					const chunks = this.chunkText(part.text, 4000);
					for (const chunk of chunks) {
						await this.apiCall("chat.postMessage", {
							channel,
							text: chunk,
							thread_ts: message.threadId,
						});
					}
					break;
				}

				case "typing": {
					// Slack doesn't have a persistent typing indicator
					break;
				}

				case "reaction": {
					if (part.messageId) {
						await this.apiCall("reactions.add", {
							channel,
							timestamp: part.messageId,
							name: this.sanitizeEmoji(part.emoji),
						});
					}
					break;
				}

				// Images and files require files.upload or blocks
			}
		}
	}

	/** Get channel profile */
	async getProfile(): Promise<ChannelProfile> {
		return {
			type: this.channelType,
			id: this.botInfo?.id ?? "unknown",
			name: this.botInfo?.name ?? "Slack Bot",
			username: this.botInfo?.name,
			connected: this.connected,
			capabilities: {
				text: true,
				images: true,
				files: true,
				reactions: true,
				threads: true,
				editMessages: true,
				deleteMessages: true,
				typing: false,
				cards: true, // Via Block Kit
				voice: false,
				maxMessageLength: 4000,
				maxFileSize: 1 * 1024 * 1024 * 1024, // 1GB for paid workspaces
			},
		};
	}

	/** Connect via Socket Mode */
	private async connectSocketMode(): Promise<void> {
		// Get WebSocket URL
		const response = await this.apiCall<{ ok: boolean; error?: string; url: string }>("apps.connections.open", {}, this.appToken!);
		if (!response.ok) {
			throw new Error(`Failed to open Slack connection: ${response.error}`);
		}

		this.socketUrl = response.url;
		console.log("Connecting to Slack Socket Mode...");

		const WebSocket = (await import("ws")).default;
		const ws = new WebSocket(this.socketUrl!);
		this.ws = ws;

		ws.on("open", () => {
			console.log("Slack Socket Mode connected");
			this.reconnectAttempts = 0;
		});

		ws.on("message", (data: Buffer) => {
			try {
				const envelope = JSON.parse(data.toString());
				this.handleSocketMessage(envelope);
			} catch (error) {
				console.error("Failed to parse Slack message:", error);
			}
		});

		ws.on("close", () => {
			console.log("Slack Socket Mode disconnected");
			this.connected = false;
			this.attemptReconnect();
		});

		ws.on("error", (error: Error) => {
			console.error("Slack Socket Mode error:", error);
			this.emitError(error);
		});
	}

	/** Handle Socket Mode message */
	private handleSocketMessage(envelope: SlackEnvelope): void {
		// Acknowledge the message
		if (envelope.envelope_id) {
			this.ws?.send(JSON.stringify({ envelope_id: envelope.envelope_id }));
		}

		// Process the payload
		if (envelope.payload) {
			this.handleEvent(envelope.payload);
		}
	}

	/** Handle Slack event */
	private handleEvent(payload: SlackEventPayload): void {
		switch (payload.type) {
			case "event_callback":
				if (payload.event) {
					this.handleEventCallback(payload.event);
				}
				break;

			case "app_rate_limited":
				console.warn("Slack rate limited:", payload);
				break;

			case "hello":
				console.log("Slack Socket Mode ready");
				this.connected = true;
				break;
		}
	}

	/** Handle event callback */
	private handleEventCallback(event: SlackEvent): void {
		switch (event.type) {
			case "message":
				this.handleMessageEvent(event as SlackMessageEvent);
				break;

			case "app_mention":
				this.handleAppMention(event as SlackAppMentionEvent);
				break;

			// Handle other events as needed
		}
	}

	/** Handle message event */
	private handleMessageEvent(event: SlackMessageEvent): void {
		// Ignore messages from self
		if (event.user === this.botInfo?.id) return;

		// Ignore bot messages
		if (event.bot_id) return;

		// Ignore message edits and deletions for now
		if (event.subtype) return;

		// Determine scope
		const scope = event.channel_type === "im" ? "dm" : "group";

		// Check access control
		if (scope === "group" && this.config.access.groupPolicy === "mention") {
			// For Slack, we'd need to check if the bot was mentioned
			// This is handled by the app_mention event, so we can ignore here
			return;
		}

		// Build sender info
		const sender: SenderInfo = {
			id: event.user,
			name: event.user_profile?.real_name ?? event.user,
			username: event.user_profile?.display_name ?? event.user,
			avatar: event.user_profile?.image_72,
			teamId: event.team,
		};

		// Build content
		let content: InboundMessage["content"];

		if (event.text) {
			// Check for commands
			if (event.text.startsWith("/")) {
				const [command, ...args] = event.text.slice(1).split(" ");
				content = {
					type: "command",
					command,
					args,
				};
			} else {
				content = {
					type: "text",
					text: this.cleanMentions(event.text),
				};
			}
		} else {
			return;
		}

		// Get agent ID
		const agentId = this.config.name;

		// Create inbound message
		const inboundMessage = this.createInboundMessage({
			id: event.ts,
			agentId,
			scope,
			identifier: event.channel,
			sender,
			content,
			threadId: event.thread_ts,
		});

		this.emitMessage(inboundMessage);
	}

	/** Handle app mention event */
	private handleAppMention(event: SlackAppMentionEvent): void {
		// Similar to message but always a mention
		if (event.user === this.botInfo?.id) return;

		const sender: SenderInfo = {
			id: event.user,
			name: event.user,
			teamId: event.team,
		};

		const content: InboundMessage["content"] = {
			type: "text",
			text: this.cleanMentions(event.text),
		};

		const agentId = this.config.name;

		const inboundMessage = this.createInboundMessage({
			id: event.ts,
			agentId,
			scope: "group",
			identifier: event.channel,
			sender,
			content,
			threadId: event.thread_ts,
		});

		this.emitMessage(inboundMessage);
	}

	/** Attempt to reconnect */
	private attemptReconnect(): void {
		if (this.reconnectAttempts < this.maxReconnectAttempts) {
			this.reconnectAttempts++;
			const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
			console.log(`Attempting to reconnect to Slack in ${delay}ms (attempt ${this.reconnectAttempts})`);
			setTimeout(() => this.connectSocketMode(), delay);
		} else {
			console.error("Max reconnection attempts reached");
			this.emitError(new Error("Failed to reconnect to Slack after maximum attempts"));
		}
	}

	/** Clean mentions from text */
	private cleanMentions(text: string): string {
		// Replace <@USER_ID> with @username or remove
		return text.replace(/<@([A-Z0-9]+)>/g, "").trim();
	}

	/** Sanitize emoji for Slack */
	private sanitizeEmoji(emoji: string): string {
		// Remove colons if present
		return emoji.replace(/:/g, "");
	}

	/** Make API call to Slack */
	private async apiCall<T = unknown>(
		method: string,
		params: Record<string, unknown> = {},
		token?: string,
	): Promise<T> {
		const url = `https://slack.com/api/${method}`;
		const authToken = token ?? this.botToken;

		const response = await fetch(url, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${authToken}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify(params),
		});

		if (!response.ok) {
			throw new Error(`Slack API error: ${response.status} ${response.statusText}`);
		}

		return response.json() as T;
	}
}

/** Register with global registry */
globalChannelRegistry.register("slack", SlackAdapter);

/** Slack API types */
interface SlackUser {
	id: string;
	name: string;
}

interface SlackEnvelope {
	envelope_id?: string;
	payload?: SlackEventPayload;
}

interface SlackEventPayload {
	type: string;
	event?: SlackMessageEvent | SlackAppMentionEvent;
	retry_attempt?: number;
}

interface SlackEvent {
	type: string;
}

interface SlackMessageEvent extends SlackEvent {
	user: string;
	channel: string;
	ts: string;
	text: string;
	team?: string;
	channel_type: string;
	thread_ts?: string;
	bot_id?: string;
	subtype?: string;
	user_profile?: {
		real_name?: string;
		display_name?: string;
		image_72?: string;
	};
}

interface SlackAppMentionEvent extends SlackEvent {
	user: string;
	channel: string;
	ts: string;
	text: string;
	team: string;
	thread_ts?: string;
}

export { SlackAdapter };
